// src/lib/journal-envois-facture.js — ce que l'écran lit du JOURNAL DES ENVOIS.
//
// ⚠️ CHEMIN COMPTABLE. Module PUR.
//
// Décision Dimitri (2026-10-03) : la facture émise n'est jamais écrite ;
// l'envoi d'une facture au propriétaire est noté dans un journal à part
// (table `audit_log`, écrit par lib/facture-envoi-proprio.js et par le renvoi
// de l'opérateur, lu par l'écran via GET /api/facture-envoi-proprio).
//
//   · `envoye` HORS essai → « Envoyé » ;
//   · `incertain` HORS essai (et aucun `envoye`) → « Envoi incertain —
//     vérifiez avant de renvoyer », SANS bouton « Envoyer » (revue argent
//     2026-10-03 : le propriétaire l'a peut-être reçue) ;
//   · un essai (envoyé à Dimitri seul) ne compte jamais ;
//   · un journal ILLISIBLE n'est pas un journal vide : « — » (on ne sait pas),
//     jamais « non envoyée ».
//
// Repère de SESSION (revue 2026-10-03) : une facture dont l'envoi vient de
// finir « parti mais non noté » ou « incertain » est bloquée dans cet onglet
// jusqu'au rechargement, même si le journal ne le dit pas (encore).

/** Marqueur : le journal n'a pas pu être lu. */
export const JOURNAL_ILLISIBLE = Object.freeze({ illisible: true });

/**
 * @param {Array<{invoice_id:string, at:string, destinataire:string|null, resultat:string, essai:boolean}>} envois
 * @returns {Object<string, {at:string, destinataire:string|null, resultat:'envoye'|'incertain'}>}
 *   par facture : le dernier « envoye » s'il y en a un, sinon le dernier « incertain ».
 */
export function indexerJournalEnvois(envois) {
  const index = {};
  for (const e of Array.isArray(envois) ? envois : []) {
    if (!e || !e.invoice_id || e.essai === true) continue;
    if (e.resultat !== "envoye" && e.resultat !== "incertain") continue;
    const cur = index[e.invoice_id];
    const plusFort =
      !cur ||
      (e.resultat === "envoye" && cur.resultat !== "envoye") ||
      (e.resultat === cur.resultat && String(e.at) > String(cur.at));
    if (plusFort)
      index[e.invoice_id] = {
        at: e.at,
        destinataire: e.destinataire || null,
        resultat: e.resultat,
      };
  }
  return index;
}

/**
 * Ce que la ligne de facture porte, d'après le journal (et le repère de session).
 * @param {object|null} journal - index (indexerJournalEnvois), JOURNAL_ILLISIBLE,
 *   ou null (pas encore lu)
 * @param {string} invoiceId
 * @param {Set<string>} [bloquesSession] - factures bloquées dans cet onglet
 * @returns {{ envoiCourriel: object|null, envoiIncertain: boolean, envoiInconnu: boolean }}
 */
export function envoiDuJournal(journal, invoiceId, bloquesSession) {
  const bloquee = !!(
    invoiceId &&
    bloquesSession &&
    typeof bloquesSession.has === "function" &&
    bloquesSession.has(invoiceId)
  );
  if (!journal || journal.illisible === true)
    return { envoiCourriel: null, envoiIncertain: bloquee, envoiInconnu: true };
  const e = (invoiceId && journal[invoiceId]) || null;
  return {
    envoiCourriel: e && e.resultat === "envoye" ? e : null,
    envoiIncertain: bloquee || !!(e && e.resultat === "incertain"),
    envoiInconnu: false,
  };
}
